import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card, Badge, Button } from '@shared/components/ui';
import {
  MapPin,
  Database,
  ArrowRight,
  Activity,
  Globe,
  Lock,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';

interface DataLocation {
  data_type: string;
  location: string;
  region: string;
  provider: string;
  description: string;
  encryption: string;
  data_residency: string;
  access_methods: string[];
}

interface DataFlow {
  source: string;
  destination: string;
  purpose: string;
  frequency: string;
}

interface DataTransferLog {
  timestamp: string;
  source: string;
  destination: string;
  data_size: string;
  purpose: string;
}

interface DataLocationsResponse {
  data_region: string;
  locations: DataLocation[];
  data_flows: DataFlow[];
  data_transfer_logs: DataTransferLog[];
  last_updated: string;
}

const fetchDataLocations = async (): Promise<DataLocationsResponse> => {
  const res = await fetch('/api/v1/compliance/data-locations', {
    headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
  });
  if (!res.ok) throw new Error(`Failed to load data locations (${res.status})`);
  return res.json();
};

const keyFor = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');

export default function DataLocations() {
  const { t, i18n } = useTranslation('compliance');
  const {
    data: locationData,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['data-locations'],
    queryFn: fetchDataLocations,
  });
  const locale = i18n.resolvedLanguage || i18n.language;
  const locations = locationData?.locations ?? [];
  const flows = locationData?.data_flows ?? [];
  const transfers = locationData?.data_transfer_logs ?? [];
  const translateValue = (section: string, value: string) => {
    const key = `dataLocations.${section}.${keyFor(value)}`;
    const translated = t(key);
    return translated === key ? value : translated;
  };
  const translateDescription = (value: string) => {
    const customStoragePrefix = 'User-configured storage destination: ';
    if (value.startsWith(customStoragePrefix)) {
      return t('dataLocations.descriptions.user_configured_storage_destination_name', {
        name: value.slice(customStoragePrefix.length).replace(/\.$/, ''),
      });
    }
    return translateValue('descriptions', value);
  };
  const formatDate = (value: string, includeTime = false) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return t('dataLocations.unknownDate');
    return new Intl.DateTimeFormat(locale, {
      dateStyle: 'medium',
      ...(includeTime ? { timeStyle: 'short' as const } : {}),
    }).format(date);
  };
  const formatTransferSize = (value: string) => {
    const backupJobs = /^(\d+)\s+backup jobs$/i.exec(value);
    return backupJobs
      ? t('dataLocations.transfers.backupJobs', { count: Number(backupJobs[1]) })
      : value;
  };

  if (isLoading) {
    return (
      <div className="flex min-h-64 items-center justify-center" role="status" aria-live="polite">
        <div className="text-center">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-b-2 border-brand-600" />
          <p className="mt-3 text-sm text-text-muted-light dark:text-text-muted-dark">
            {t('dataLocations.loading')}
          </p>
        </div>
      </div>
    );
  }

  if (isError || !locationData) {
    return (
      <Card className="mx-auto max-w-2xl space-y-4 p-6 text-center sm:p-8">
        <div role="alert" className="text-sm text-state-danger-light dark:text-state-danger-dark">
          {t('dataLocations.errors.loadFailed')}
        </div>
        <Button variant="secondary" onClick={() => void refetch()} isLoading={isFetching}>
          <RefreshCw aria-hidden="true" className="mr-2 h-4 w-4" />
          {t('dataLocations.retry')}
        </Button>
      </Card>
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl space-y-6 pb-2 sm:space-y-8">
      <header className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-text-primary-light dark:text-text-primary-dark sm:text-3xl">
            {t('dataLocations.title')}
          </h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-text-muted-light dark:text-text-muted-dark sm:text-base">
            {t('dataLocations.subtitle')}
          </p>
        </div>
        <Badge
          variant="info"
          className="min-h-9 w-fit max-w-full whitespace-normal px-3 py-2 text-xs sm:text-sm"
        >
          {t('dataLocations.updatedAt', { date: formatDate(locationData.last_updated, true) })}
        </Badge>
      </header>

      <section className="relative overflow-hidden rounded-xl border border-brand-600 bg-brand-600 text-white shadow-soft dark:border-brand-500 dark:bg-brand-700">
        <Globe
          aria-hidden="true"
          size={200}
          className="absolute -bottom-12 -right-10 rotate-12 opacity-10 sm:size-60"
        />
        <div className="relative z-10 space-y-3 p-5 sm:p-8">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-brand-50">
            {t('dataLocations.primaryRegion')}
          </p>
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            {translateValue('regions', locationData.data_region)}
          </h2>
          <p className="max-w-3xl text-sm leading-6 text-brand-50 sm:text-base">
            {t('dataLocations.regionDescription')}
          </p>
        </div>
      </section>

      <section aria-labelledby="data-location-heading" className="space-y-4">
        <h2
          id="data-location-heading"
          className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark"
        >
          {t('dataLocations.locationsTitle')}
        </h2>
        {locations.length === 0 ? (
          <Card className="p-6 text-sm text-text-muted-light dark:text-text-muted-dark">
            {t('dataLocations.empty.locations')}
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            {locations.map((location, index) => (
              <Card
                key={`${location.data_type}-${location.provider}-${index}`}
                className="min-w-0 space-y-5 p-4 sm:p-6"
              >
                <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-surface-2-light text-brand-600 dark:bg-surface-2-dark dark:text-brand-400">
                      <Database aria-hidden="true" size={21} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="break-words text-base font-semibold text-text-primary-light dark:text-text-primary-dark">
                        {translateValue('dataTypes', location.data_type)}
                      </h3>
                      <p className="mt-0.5 break-words text-sm text-text-muted-light dark:text-text-muted-dark">
                        {translateValue('providers', location.provider)}
                      </p>
                    </div>
                  </div>
                  <Badge variant="success" className="min-h-7 w-fit max-w-full whitespace-normal">
                    {t('dataLocations.residency', {
                      region: translateValue('regions', location.data_residency),
                    })}
                  </Badge>
                </div>

                {location.description && (
                  <p className="text-sm leading-5 text-text-muted-light dark:text-text-muted-dark">
                    {translateDescription(location.description)}
                  </p>
                )}

                <dl className="space-y-3">
                  <InfoRow
                    icon={<MapPin aria-hidden="true" size={15} />}
                    label={t('dataLocations.regionLabel')}
                    value={`${location.location} (${location.region})`}
                  />
                  <InfoRow
                    icon={<Lock aria-hidden="true" size={15} />}
                    label={t('dataLocations.encryptionLabel')}
                    value={translateValue('encryption', location.encryption)}
                  />
                </dl>

                {(location.access_methods || []).length > 0 && (
                  <div className="border-t border-surface-border-light pt-4 dark:border-surface-border-dark">
                    <p className="mb-2 text-xs font-semibold text-text-muted-light dark:text-text-muted-dark">
                      {t('dataLocations.accessMethods')}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {(location.access_methods || []).map((method, methodIndex) => (
                        <span
                          key={`${method}-${methodIndex}`}
                          className="rounded-md border border-surface-border-light bg-surface-2-light px-2 py-1 text-xs text-text-muted-light dark:border-surface-border-dark dark:bg-surface-2-dark dark:text-text-muted-dark"
                        >
                          {translateValue('accessMethodsLabels', method)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </section>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Card className="overflow-hidden">
          <div className="border-b border-surface-border-light bg-surface-2-light p-4 dark:border-surface-border-dark dark:bg-surface-2-dark sm:p-5">
            <h2 className="flex items-center gap-2 text-base font-semibold text-text-primary-light dark:text-text-primary-dark">
              <Activity
                aria-hidden="true"
                size={18}
                className="text-brand-600 dark:text-brand-400"
              />
              {t('dataLocations.flows.title')}
            </h2>
          </div>
          {flows.length === 0 ? (
            <p className="p-5 text-sm text-text-muted-light dark:text-text-muted-dark">
              {t('dataLocations.empty.flows')}
            </p>
          ) : (
            <div className="max-h-[420px] divide-y divide-surface-border-light overflow-y-auto dark:divide-surface-border-dark">
              {flows.map((flow, index) => (
                <div key={`${flow.source}-${flow.destination}-${index}`} className="space-y-2 p-4">
                  <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm font-semibold text-text-primary-light dark:text-text-primary-dark">
                    <span className="break-words">{translateValue('flowNodes', flow.source)}</span>
                    <ArrowRight
                      aria-hidden="true"
                      className="h-4 w-4 shrink-0 text-text-subtle-light dark:text-text-subtle-dark"
                    />
                    <span className="break-words">
                      {translateValue('flowNodes', flow.destination)}
                    </span>
                  </div>
                  <p className="text-sm leading-5 text-text-muted-light dark:text-text-muted-dark">
                    {translateValue('flowPurposes', flow.purpose)}
                  </p>
                  <p className="text-xs text-text-subtle-light dark:text-text-subtle-dark">
                    {t('dataLocations.flows.frequency')}:{' '}
                    {translateValue('flowFrequencies', flow.frequency)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="overflow-hidden">
          <div className="border-b border-surface-border-light bg-surface-2-light p-4 dark:border-surface-border-dark dark:bg-surface-2-dark sm:p-5">
            <h2 className="flex items-center gap-2 text-base font-semibold text-text-primary-light dark:text-text-primary-dark">
              <ShieldCheck
                aria-hidden="true"
                size={18}
                className="text-brand-600 dark:text-brand-400"
              />
              {t('dataLocations.transfers.title')}
            </h2>
          </div>
          {transfers.length === 0 ? (
            <p className="p-5 text-sm text-text-muted-light dark:text-text-muted-dark">
              {t('dataLocations.empty.transfers')}
            </p>
          ) : (
            <div className="max-h-[420px] divide-y divide-surface-border-light overflow-y-auto dark:divide-surface-border-dark">
              {transfers.map((transfer, index) => (
                <article
                  key={`${transfer.timestamp}-${transfer.source}-${index}`}
                  className="space-y-2 p-4"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <time
                      className="text-xs text-text-subtle-light dark:text-text-subtle-dark"
                      dateTime={transfer.timestamp}
                    >
                      {formatDate(transfer.timestamp, true)}
                    </time>
                    <span className="rounded-md bg-accent-soft-light px-2 py-1 text-xs font-medium text-brand-700 dark:bg-accent-soft-dark dark:text-brand-300">
                      {formatTransferSize(transfer.data_size)}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
                    {translateValue('transferPurposes', transfer.purpose)}
                  </p>
                  <p className="flex min-w-0 flex-wrap items-center gap-2 text-xs text-text-muted-light dark:text-text-muted-dark">
                    <span className="break-words">
                      {translateValue('flowNodes', transfer.source)}
                    </span>
                    <ArrowRight aria-hidden="true" className="h-3 w-3 shrink-0" />
                    <span className="break-words">
                      {translateValue('flowNodes', transfer.destination)}
                    </span>
                  </p>
                </article>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card className="flex items-start gap-3 p-4 sm:p-5">
        <Lock
          aria-hidden="true"
          size={18}
          className="mt-0.5 shrink-0 text-text-subtle-light dark:text-text-subtle-dark"
        />
        <div>
          <h2 className="text-sm font-semibold text-text-primary-light dark:text-text-primary-dark">
            {t('dataLocations.securityNote.title')}
          </h2>
          <p className="mt-1 text-sm leading-5 text-text-muted-light dark:text-text-muted-dark">
            {t('dataLocations.securityNote.description')}
          </p>
        </div>
      </Card>
    </div>
  );
}

function InfoRow({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="flex min-w-0 items-start justify-between gap-3 text-sm">
      <dt className="flex shrink-0 items-center gap-2 text-text-muted-light dark:text-text-muted-dark">
        {icon}
        {label}
      </dt>
      <dd className="min-w-0 break-words text-right font-medium text-text-primary-light dark:text-text-primary-dark">
        {value}
      </dd>
    </div>
  );
}
